﻿import {
  addProjectTechnician, removeProjectTechnician,
  addProjectTechDate, removeProjectTechDate,
  updateProject, deleteProject, deleteSubtask, restoreProject,
  createSubtasks, removeSubtaskTechniciansByProjectAndTech,
} from './lib/api'
import { generateQASubtasks, QA_MILESTONE_NAMES, getQASeededSet, markQASeeded } from './lib/qaMilestones'
import { useState, useMemo, useRef, useEffect, Suspense } from 'react'
import { lazyWithReload } from './lib/lazyWithReload'
import { useProjects } from './hooks/useProjects'
import { useInspectionDueDates } from './hooks/useInspectionDueDates'
import { useToleranceExpiryDates } from './hooks/useToleranceExpiryDates'
import { AuthProvider, useAuth } from './hooks/useAuth'
import { FLEET_REGS } from './lib/fleetUtilization'
import { logChange } from './lib/auditLog'
import ProjectTable from './components/ProjectTable'
import ProjectModal from './components/ProjectModal'
import SubtaskModal from './components/SubtaskModal'
import GanttChart from './components/GanttChart'
import DetailDrawer from './components/DetailDrawer'
import AircraftRegistry from './components/AircraftRegistry'
import TechnicianRegistry from './components/TechnicianRegistry'
import CalendarView from './components/CalendarView'
import DepartmentCalendarView from './components/DepartmentCalendarView'
import LoginPage from './components/LoginPage'
import SetPasswordPage from './components/SetPasswordPage'
import ProjectHistoryDrawer from './components/ProjectHistoryDrawer'
import EBISWorkOrderPanel from './components/EBISWorkOrderPanel'
import DeletedProjectsModal from './components/DeletedProjectsModal'
import MyTasksTab from './components/MyTasksTab'
import DailyDigestModal, { shouldShowDailyDigest, markDailyDigestShown } from './components/DailyDigestModal'
import { useTaskNotifications } from './hooks/useTaskNotifications'
import ProjectNotesModal from './components/ProjectNotesModal'
import ProfilePage from './components/ProfilePage'
import IdleWarningModal from './components/IdleWarningModal'
import SubtaskNoteSheet from './components/SubtaskNoteSheet'


const QuotingPage           = lazyWithReload(() => import('./components/QuotingPage'))
const FleetDueListPage      = lazyWithReload(() => import('./components/FleetDueListPage'))
const CapacityPage          = lazyWithReload(() => import('./components/CapacityPage'))
const TrainingPage          = lazyWithReload(() => import('./components/TrainingPage'))
const ReportsPage           = lazyWithReload(() => import('./components/ReportsPage'))
const SchedulingDashboardPage = lazyWithReload(() => import('./components/SchedulingDashboardPage'))
const CustomerPortalPage    = lazyWithReload(() => import('./components/CustomerPortalPage'))
const CustomerPortal        = lazyWithReload(() => import('./components/portal/CustomerPortal'))
const EquipmentPage         = lazyWithReload(() => import('./components/EquipmentPage'))
const HangarStackPage       = lazyWithReload(() => import('./components/HangarStackPage'))
const SettingsPage          = lazyWithReload(() => import('./components/SettingsPage'))
const PlatformAdminPage     = lazyWithReload(() => import('./components/superadmin/SuperAdminApp'))
import { useIdleTimeout } from './hooks/useIdleTimeout'

export { DEPT_COLORS, DEPT_PRIORITY } from './lib/deptColors'

const INTERNAL_TIMEOUT_MS = 3 * 60 * 60 * 1000  // 3 hours

function AuthenticatedApp() {
  const { user, profile, signOut, isEditor, isSubtaskEditor, isLineService, isTechnician, isInspector, canAccessEquipment: _canAccessEquipment, allowedModule, isSuperAdmin, hasFeature } = useAuth()

  const { showWarning, secondsLeft, stayLoggedIn } = useIdleTimeout({
    timeoutMs: INTERNAL_TIMEOUT_MS,
    warningMs: 60_000,
    onSignOut: signOut,
    enabled: !!user,
    storageKey: 'idle_internal',
  })
  const canEditSubtasks = isEditor || isSubtaskEditor
  const canAccessDashboard  = !isLineService && allowedModule !== 'training'
  const canAccessEquipment  = _canAccessEquipment

  const { projects, subtasks, aircraft, callouts, technicians, subtaskTechnicians, projectTechnicians, projectTechDates, flightLegs, loading, refetch: fetchData, loadProjectSubtasks } = useProjects()

  // Apply org scheduling defaults whenever the saved settings actually change
  // (covers both initial load and same-session settings saves)
  const prevSchedSettingsKey = useRef(null)
  useEffect(() => {
    if (!profile) return
    const sched = profile?.orgs?.settings?.scheduling
    if (!sched) return
    const key = JSON.stringify({ defaultView: sched.defaultView, tableFilters: sched.tableFilters })
    if (prevSchedSettingsKey.current === key) return
    prevSchedSettingsKey.current = key
    if (sched.defaultView) setMsView(sched.defaultView)
    if (sched.tableFilters?.types?.length)       setTableTypeFilter(new Set(sched.tableFilters.types))
    if (sched.tableFilters?.departments?.length) setTableDeptFilter(new Set(sched.tableFilters.departments))
    if (sched.tableFilters?.statuses?.length)    setTableStatusFilter(new Set(sched.tableFilters.statuses))
  }, [profile])

  // ── Dashboard role detection (must come after useProjects so technicians is in scope) ──
  const linkedTech = useMemo(
    () => technicians.find(t => t.profile_id === user?.id) ?? null,
    [technicians, user?.id]
  )

  const dashboardRole = useMemo(() => {
    if (!profile) return null
    if (allowedModule === 'training') return 'hr'
    if (!isEditor) return linkedTech?.department === 'QA' ? 'qa' : 'tech'
    return 'manager'
  }, [profile, isEditor, allowedModule, linkedTech])

  const fleetRegs = useMemo(
    () => aircraft.filter(a => a.registration && a.traxxall_enrolled && FLEET_REGS.has(a.registration)).map(a => a.registration),
    [aircraft]
  )
  const inspectionDueDates    = useInspectionDueDates(fleetRegs)
  const toleranceExpiryDates  = useToleranceExpiryDates(fleetRegs)

  // Build a map: subtaskId → array of technician objects
  const subtaskTechMap = useMemo(() => {
    const bySubtask = {}
    subtaskTechnicians.forEach(st => {
      const tech = technicians.find(t => t.id === st.technician_id)
      if (!tech) return
      if (!bySubtask[st.subtask_id]) bySubtask[st.subtask_id] = new Map()
      bySubtask[st.subtask_id].set(tech.id, tech)
    })
    return Object.fromEntries(
      Object.entries(bySubtask).map(([sid, m]) => [sid, [...m.values()]])
    )
  }, [subtaskTechnicians, technicians])

  // Build a map: projectId → deduplicated array of all technician objects
  // (union of subtask-assigned techs + project-level techs)
  const projectTechMap = useMemo(() => {
    const map = {} // projectId → Map<techId, tech>
    // From subtask assignments
    subtasks.forEach(s => {
      const techs = subtaskTechMap[s.id] || []
      techs.forEach(tech => {
        if (!map[s.project_id]) map[s.project_id] = new Map()
        map[s.project_id].set(tech.id, tech)
      })
    })
    // From project-level assignments
    projectTechnicians.forEach(pt => {
      const tech = technicians.find(t => t.id === pt.technician_id)
      if (!tech) return
      if (!map[pt.project_id]) map[pt.project_id] = new Map()
      map[pt.project_id].set(tech.id, tech)
    })
    // Convert inner Maps to sorted arrays
    return Object.fromEntries(
      Object.entries(map).map(([pid, techMap]) => [
        pid,
        [...techMap.values()].sort((a, b) => a.last_name.localeCompare(b.last_name)),
      ])
    )
  }, [subtasks, subtaskTechMap, projectTechnicians, technicians])

  // Set of techIds that have project-level assignments (for showing × in header)
  const projectTechSet = useMemo(() => {
    const set = {}
    projectTechnicians.forEach(pt => {
      if (!set[pt.project_id]) set[pt.project_id] = new Set()
      set[pt.project_id].add(pt.technician_id)
    })
    return set
  }, [projectTechnicians])

  // Batch-seed QA subtasks for all active projects so they appear on the QA
  // department calendar without requiring each project's workflow to be opened.
  // Seed QA Incoming/RTS subtasks for all active projects that don't have any yet.
  // Never overwrites — manual edits and deletions are preserved.
  const qaBatchSyncedRef = useRef('')
  useEffect(() => {
    // Legacy seeder: only runs for orgs that pre-date the auto-milestones settings feature
    // (i.e., orgs where autoMilestones has never been configured). New orgs get
    // autoMilestones: [] on provisioning, which skips this block entirely.
    if (profile?.orgs?.settings?.autoMilestones !== undefined) return
    if (loading || !isEditor || projects.length === 0) return
    const activeProjects = projects.filter(p =>
      (p.status === 'In Work' || p.status === 'Scheduled') &&
      p.arrival_date && p.departure_date
    )
    const syncKey = activeProjects.map(p => `${p.id}|${p.arrival_date}|${p.departure_date}`).join(',')
    if (qaBatchSyncedRef.current === syncKey) return
    qaBatchSyncedRef.current = syncKey

    const seeded = getQASeededSet()
    for (const project of activeProjects) {
      const existing = subtasks.filter(s =>
        s.project_id === project.id && s.department === 'QA' && QA_MILESTONE_NAMES.has(s.task_name)
      )
      if (existing.length > 0) {
        markQASeeded(project.id)
        continue
      }
      // Project was previously seeded and now has none — user deleted them intentionally
      if (seeded.has(project.id)) continue
      const expected = generateQASubtasks(project.id, project.arrival_date, project.departure_date)
      if (expected.length === 0) continue
      createSubtasks(expected)
        .then(() => { markQASeeded(project.id); fetchData() })
        .catch(err => console.error('[QA batch seed]', err))
    }
  }, [loading, projects, subtasks, isEditor])

  // Apply org brand colors as CSS custom properties whenever the profile loads.
  useEffect(() => {
    const brand = profile?.orgs?.settings?.brand
    const root = document.documentElement
    if (!brand) {
      root.style.removeProperty('--brand-primary-rgb')
      root.style.removeProperty('--brand-primary-dark-rgb')
      root.style.removeProperty('--brand-accent-rgb')
      root.style.removeProperty('--brand-secondary-rgb')
      root.style.removeProperty('--brand-nav-rgb')
      return
    }
    const hexToRgb = (hex) => {
      const h = (hex ?? '').replace('#', '')
      if (h.length !== 6) return null
      return `${parseInt(h.slice(0, 2), 16)} ${parseInt(h.slice(2, 4), 16)} ${parseInt(h.slice(4, 6), 16)}`
    }
    const darken = (hex, amount = 30) => {
      const h = (hex ?? '').replace('#', '')
      if (h.length !== 6) return null
      const clamp = (n) => Math.max(0, Math.min(255, n))
      const r = clamp(parseInt(h.slice(0, 2), 16) - amount)
      const g = clamp(parseInt(h.slice(2, 4), 16) - amount)
      const b = clamp(parseInt(h.slice(4, 6), 16) - amount)
      return `${r} ${g} ${b}`
    }
    const accentFromPrimary = (hex) => {
      const h = (hex ?? '').replace('#', '')
      if (h.length !== 6) return null
      const tint = ch => Math.round(parseInt(ch, 16) * 0.1 + 255 * 0.9)
      return `${tint(h.slice(0, 2))} ${tint(h.slice(2, 4))} ${tint(h.slice(4, 6))}`
    }
    const set = (varName, val) => { if (val) root.style.setProperty(varName, val) }
    set('--brand-primary-rgb',      hexToRgb(brand.primary))
    set('--brand-primary-dark-rgb', darken(brand.primary))
    set('--brand-accent-rgb',       accentFromPrimary(brand.primary))
    set('--brand-secondary-rgb',    hexToRgb(brand.secondary))
    set('--brand-nav-rgb',          hexToRgb(brand.nav))
  }, [profile?.orgs?.settings?.brand])

  const addProjectTech = async (projectId, techId) => {
    const tech = technicians.find(t => t.id === techId)
    const techName = tech ? `${tech.first_name} ${tech.last_name}` : techId
    const project = projects.find(p => p.id === projectId)
    await addProjectTechnician(projectId, techId)
    await logChange({
      projectId, entityType: 'project', entityId: projectId,
      entityLabel: project?.aircraft?.registration || project?.work_order,
      action: 'updated',
      changedFields: [{ field: 'Technician Added', from: '—', to: techName }],
      userProfile: profile,
    })
    fetchData()
  }

  const removeProjectTech = async (projectId, techId) => {
    const tech = technicians.find(t => t.id === techId)
    const techName = tech ? `${tech.first_name} ${tech.last_name}` : techId
    const project = projects.find(p => p.id === projectId)
    await removeProjectTechnician(projectId, techId)
    const projectSubtaskIds = subtasks.filter(s => s.project_id === projectId).map(s => s.id)
    await removeSubtaskTechniciansByProjectAndTech(projectSubtaskIds, techId)
    await logChange({
      projectId, entityType: 'project', entityId: projectId,
      entityLabel: project?.aircraft?.registration || project?.work_order,
      action: 'updated',
      changedFields: [{ field: 'Technician Removed', from: techName, to: '—' }],
      userProfile: profile,
    })
    fetchData()
  }

  const handleAddTechDate = async (projectId, techId, startDate, endDate) => {
    const tech = technicians.find(t => t.id === techId)
    const techName = tech ? `${tech.first_name} ${tech.last_name}` : techId
    const project = projects.find(p => p.id === projectId)
    await addProjectTechDate(projectId, techId, startDate, endDate)
    const dateLabel = `${startDate || '?'} – ${endDate || '?'}`
    await logChange({
      projectId, entityType: 'project', entityId: projectId,
      entityLabel: project?.aircraft?.registration || project?.work_order,
      action: 'updated',
      changedFields: [{ field: `${techName} Dates`, from: '—', to: dateLabel }],
      userProfile: profile,
    })
    fetchData()
  }

  const handleRemoveTechDate = async (id) => {
    const entry = projectTechDates.find(d => d.id === id)
    await removeProjectTechDate(id)
    if (entry) {
      const tech = technicians.find(t => t.id === entry.technician_id)
      const techName = tech ? `${tech.first_name} ${tech.last_name}` : entry.technician_id
      const project = projects.find(p => p.id === entry.project_id)
      const dateLabel = `${entry.start_date || '?'} – ${entry.end_date || '?'}`
      await logChange({
        projectId: entry.project_id, entityType: 'project', entityId: entry.project_id,
        entityLabel: project?.aircraft?.registration || project?.work_order,
        action: 'updated',
        changedFields: [{ field: `${techName} Dates Removed`, from: dateLabel, to: '—' }],
        userProfile: profile,
      })
    }
    fetchData()
  }

  const [isRefreshing,    setIsRefreshing]    = useState(false)
  const [taskRefreshKey,  setTaskRefreshKey]  = useState(0)
  const handleRefresh = async () => {
    if (isRefreshing) return
    setIsRefreshing(true)
    setTaskRefreshKey(k => k + 1)
    try { await fetchData() } finally { setIsRefreshing(false) }
  }

  const [tableSearch, setTableSearch] = useState('')
  const [tableStatusFilter, setTableStatusFilter] = useState(new Set())
  const toggleTableStatus = (s) => {
    setTableStatusFilter(prev => { const n = new Set(prev); n.has(s) ? n.delete(s) : n.add(s); return n })
  }
  const [tableDeptFilter, setTableDeptFilter] = useState(new Set())
  const [tableTypeFilter, setTableTypeFilter] = useState(new Set())
  const toggleTableDept = (d) => {
    setTableDeptFilter(prev => { const n = new Set(prev); n.has(d) ? n.delete(d) : n.add(d); return n })
  }
  const toggleTableType = (t) => {
    setTableTypeFilter(prev => { const n = new Set(prev); n.has(t) ? n.delete(t) : n.add(t); return n })
  }
  const [ganttSearch, setGanttSearch] = useState('')
  const clearTableFilters = () => {
    setTableSearch('')
    setTableStatusFilter(new Set())
    setTableDeptFilter(new Set())
    setTableTypeFilter(new Set())
  }

  const refetch = async () => { await fetchData() }

  const openNotesModal = (projectId, reg, { required = false, prompt = null } = {}) => {
    setNotesModal({ projectId, reg, required, prompt })
  }

  // Push current view onto history stack and apply a partial nav state
  const navigate = (partial) => {
    setNavHistory(h => [...h, { page, msView, calendarSubView, deptSubView }])
    setNavFuture([])
    if (partial.page !== undefined) {
      sessionStorage.setItem('nav_reload_page', partial.page)
      setPage(partial.page)
    }
    if (partial.msView          !== undefined) setMsView(partial.msView)
    if (partial.calendarSubView !== undefined) setCalendarSubView(partial.calendarSubView)
    if (partial.deptSubView     !== undefined) setDeptSubView(partial.deptSubView)
  }
  const goBack = () => {
    if (!navHistory.length) return
    const prev = navHistory[navHistory.length - 1]
    setNavFuture(f => [{ page, msView, calendarSubView, deptSubView }, ...f])
    setNavHistory(h => h.slice(0, -1))
    setPage(prev.page)
    setMsView(prev.msView)
    setCalendarSubView(prev.calendarSubView)
    setDeptSubView(prev.deptSubView)
  }
  const goForward = () => {
    if (!navFuture.length) return
    const next = navFuture[0]
    setNavHistory(h => [...h, { page, msView, calendarSubView, deptSubView }])
    setNavFuture(f => f.slice(1))
    setPage(next.page)
    setMsView(next.msView)
    setCalendarSubView(next.calendarSubView)
    setDeptSubView(next.deptSubView)
  }

  // Navigate to the Workflows tab with a specific project selected
  const goToWorkflow = (projectId) => {
    sessionStorage.setItem('selectedProjectId', projectId)
    navigate({ page: 'master-schedule', msView: 'workflows', calendarSubView: 'project' })
  }

  const updateProjectDates = async (projectId, arrivalDate, departureDate) => {
    const existing = projects.find(p => p.id === projectId)
    const now = new Date().toISOString()
    const changes = { arrival_date: arrivalDate, departure_date: departureDate }
    if (existing?.arrival_date !== arrivalDate) changes.arrival_date_changed_at = now
    if (existing?.departure_date !== departureDate) changes.departure_date_changed_at = now
    await updateProject(projectId, changes)
    refetch()
  }

  const [page, setPage] = useState(() => {
    // Restore page after a lazyWithReload chunk-reload, then clear so normal
    // refreshes start from master-schedule (redirect effect corrects from there).
    const saved = sessionStorage.getItem('nav_reload_page')
    if (saved) { sessionStorage.removeItem('nav_reload_page'); return saved }
    return 'master-schedule'
  })
  const [deepLinkAircraftId, setDeepLinkAircraftId] = useState(null)
  const [msView, setMsView] = useState('gantt')
  const [navHistory, setNavHistory] = useState([])
  const [navFuture,  setNavFuture]  = useState([])

  // One-time URL deep-link handler (e.g. from email buttons)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const deepPage = params.get('page')
    const deepAcId = params.get('aircraft_id')
    if (deepPage === 'customer-portal') {
      setPage('customer-portal')
      if (deepAcId) setDeepLinkAircraftId(deepAcId)
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])
  const [ebisWorkOrder, setEbisWorkOrder] = useState(null)
  const [ebisHighlightItem, setEbisHighlightItem] = useState(null)
  const [ebisProjectId, setEbisProjectId] = useState(null)

  // Unified WO opener — accepts a plain string WO number or { workOrderNumber, highlightItemNum }
  // projectIdHint: caller can pass project.id directly to skip the find() lookup
  const openWorkOrder = (arg, projectIdHint = null) => {
    if (!arg) return
    const woNum = typeof arg === 'string' ? arg : arg.workOrderNumber
    const proj = projectIdHint
      ? { id: projectIdHint }
      : projects.find(p => p.work_order === woNum || (p.work_orders_extra || []).includes(woNum))
    setEbisProjectId(proj?.id ?? null)
    if (typeof arg === 'string') {
      setEbisWorkOrder(arg)
      setEbisHighlightItem(null)
    } else {
      setEbisWorkOrder(arg.workOrderNumber)
      setEbisHighlightItem(arg.highlightItemNum ?? null)
    }
  }
  const [calendarSubView, setCalendarSubView] = useState('project')
  const [deptSubView, setDeptSubView] = useState('monthly')
  const [showProjectModal, setShowProjectModal] = useState(false)
  const [showDeletedProjects, setShowDeletedProjects] = useState(false)
  const [showLogoMenu, setShowLogoMenu] = useState(false)
  const [dataExpanded, setDataExpanded] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showDailyDigest, setShowDailyDigest] = useState(false)
  const logoMenuRef = useRef(null)
  const userMenuRef = useRef(null)
  const { unreadCount, markAllSeen } = useTaskNotifications()
  const [editProject, setEditProject] = useState(null)
  const [projectPrefill, setProjectPrefill] = useState(null)
  const [editSubtask, setEditSubtask] = useState(null)
  const [addSubtaskForProject, setAddSubtaskForProject] = useState(null)
  const [drawerItem, setDrawerItem] = useState(null)
  const [historyDrawer, setHistoryDrawer]   = useState(null) // { projectId, projectLabel }
  const [snapshotForPrint, setSnapshotForPrint] = useState(null)
  const [notesModal, setNotesModal]     = useState(null)  // { projectId, reg, required, prompt }
  const [notesKey, setNotesKey]         = useState(0)
  const [subtaskNoteSheet, setSubtaskNoteSheet] = useState(null) // { subtask, projectId }
  const [filters, setFilters] = useState({ status: 'All', department: 'All', search: '' })

  const filteredProjects = projects.filter(p => {
    if (filters.status !== 'All' && p.status !== filters.status) return false
    if (filters.search) {
      const reg  = (p.aircraft?.registration || p.aircraft_registration || '').toLowerCase()
      const alt  = (p.secondary_registration || '').toLowerCase()
      const term = filters.search.toLowerCase()
      if (!reg.includes(term) && !alt.includes(term)) return false
    }
    if (filters.department !== 'All') {
      const projectSubtasks = subtasks.filter(s => s.project_id === p.id)
      const deptMatch = (p.departments || []).includes(filters.department) ||
        projectSubtasks.some(s => s.department === filters.department)
      if (!deptMatch) return false
    }
    return true
  })

  const openDrawer = (type, data, showColorPicker = false) => {
    setDrawerItem({ type, data, showColorPicker })
  }

  const handleAddToSchedule = (prefillData) => {
    setEditProject(null)
    setProjectPrefill(prefillData)
    setShowProjectModal(true)
  }

  // Close logo menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (logoMenuRef.current && !logoMenuRef.current.contains(e.target)) setShowLogoMenu(false)
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setShowUserMenu(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Daily digest — show once per day on first master-schedule/dashboard load.
  // Wait until the page has settled (navHistory.length reflects intentional nav).
  // Skip for technicians — they have a simplified view and don't need the digest.
  const DAILY_DIGEST_ENABLED = true
  useEffect(() => {
    // Technicians land on scheduling-dashboard, so only fire the digest there.
    // Everyone else fires on master-schedule or scheduling-dashboard.
    const digestPage = isTechnician
      ? page === 'scheduling-dashboard'
      : (page === 'master-schedule' || page === 'scheduling-dashboard')
    if (DAILY_DIGEST_ENABLED && !loading && profile && !isLineService && allowedModule !== 'training' &&
        digestPage && user && shouldShowDailyDigest(user.id)) {
      setShowDailyDigest(true)
    }
  }, [page, user?.id, profile, isTechnician, loading])

  useEffect(() => {
    if (allowedModule && page !== allowedModule) {
      setPage(allowedModule)
      return
    }
    if (isLineService && !isEditor && page !== 'equipment') {
      setPage('equipment')
      return
    }
    const restricted = ['capacity', 'fleet-due-list', 'aircraft', 'quoting', 'technicians', 'settings']
    if (!isEditor && restricted.includes(page)) {
      setPage('master-schedule')
    }
    if ((page === 'customer-accounts' || page === 'customer-approvals') && !isEditor) {
      setPage('master-schedule')
    }
    if (page === 'scheduling-dashboard' && !canAccessDashboard) {
      setPage('master-schedule')
    }
    // Initial landing page: org-configured per role takes priority, then hardcoded defaults.
    if (navHistory.length === 0 && page === 'master-schedule') {
      const roleLandingPages = profile?.orgs?.settings?.scheduling?.roleLandingPages ?? {}
      const roleLanding = roleLandingPages[profile?.role]
      if (roleLanding) {
        setPage(roleLanding)
        return
      }
      if (isTechnician || isSubtaskEditor) {
        setPage('scheduling-dashboard')
        return
      }
    }
    // Technician page restrictions (independent of landing page)
    if (isTechnician) {
      const techPages = ['scheduling-dashboard', 'master-schedule', 'training', 'profile', 'tasks']
      if (!techPages.includes(page)) setPage('scheduling-dashboard')
      if (['table', 'departments'].includes(msView)) setMsView('gantt')
      if (['charts', 'customer-update', 'due-list'].includes(calendarSubView)) setCalendarSubView('project')
    }
  }, [isEditor, canAccessDashboard, allowedModule, isTechnician, isSubtaskEditor, page, msView, calendarSubView, navHistory.length, profile])

  // Hold rendering until profile resolves so the correct landing page is
  // used from the first paint — avoids the master-schedule nav flash for
  // users who get redirected elsewhere (technicians, line_service, etc.).
  if (!profile) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
    </div>
  )

  if (page === 'platform-admin') {
    return <Suspense fallback={null}><PlatformAdminPage /></Suspense>
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div id="app-header" className="no-print bg-brand-black px-3 md:px-6 py-3 flex items-center justify-between shadow-md">

        {/* Logo — click to open utility panel */}
        <div className="relative" ref={logoMenuRef}>
          <button
            onClick={() => { if (!allowedModule) setShowLogoMenu(prev => !prev) }}
            className={`flex items-center gap-3 ${allowedModule ? 'cursor-default' : 'group'}`}
          >
            <img
              src={profile?.orgs?.settings?.logo_url ?? '/logo-alt.png'}
              alt={profile?.orgs?.name ?? 'Logo'}
              className="h-8 w-auto"
              onError={e => { e.currentTarget.src = '/logo-alt.png' }}
            />
            <div className="hidden sm:block w-px h-10 bg-white/20 mx-1" />
            <span className="hidden sm:inline text-white/50 text-xs font-body tracking-widest uppercase group-hover:text-white/70 transition-colors">
              {{'master-schedule': 'Master Schedule', capacity: 'Capacity', aircraft: 'Aircraft', quoting: 'Quoting', 'fleet-due-list': 'Fleet Due List', training: 'Training & Compliance', tasks: 'My Tasks', 'scheduling-dashboard': 'Dashboard', 'hangar-stack': 'Hangar Stack', settings: 'Settings', 'platform-admin': isSuperAdmin ? 'Platform Admin' : 'Org Settings'}[page] ?? 'Master Schedule'}
            </span>
            {!allowedModule && (
              <svg className={`w-3 h-3 text-white/30 group-hover:text-white/50 transition-transform ${showLogoMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            )}
          </button>

          {showLogoMenu && (
            <div className="absolute left-0 top-full mt-2 w-56 bg-brand-black border border-white/10 rounded-xl shadow-2xl z-50">
              {/* Main pages */}
              <div className="p-2">
                {!isLineService && <button
                  onClick={() => { navigate({ page: 'master-schedule' }); setShowLogoMenu(false) }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-left ${page === 'master-schedule' ? 'text-white bg-white/15' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Master Schedule
                </button>}
                {isEditor && (
                  <button
                    onClick={() => { navigate({ page: 'capacity' }); setShowLogoMenu(false) }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-left ${page === 'capacity' ? 'text-white bg-white/15' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Capacity
                  </button>
                )}
                {isEditor && hasFeature('traxxall') && (
                  <button
                    onClick={() => { navigate({ page: 'fleet-due-list' }); setShowLogoMenu(false) }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-left ${page === 'fleet-due-list' ? 'text-white bg-white/15' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    Fleet Due List
                  </button>
                )}
                {!isLineService && hasFeature('training') && <button
                  onClick={() => { navigate({ page: 'training' }); setShowLogoMenu(false) }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-left ${page === 'training' ? 'text-white bg-white/15' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                  </svg>
                  Training & Compliance
                </button>}
                {canAccessDashboard && (
                  <button
                    onClick={() => { navigate({ page: 'scheduling-dashboard' }); setShowLogoMenu(false) }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-left ${page === 'scheduling-dashboard' ? 'text-white bg-white/15' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                    </svg>
                    Dashboard
                  </button>
                )}
              </div>

              {isEditor && (
                <>
                  <div className="mx-2 border-t border-white/10" />

                  {/* Data section — collapsible */}
                  <div className="p-2">
                    <button
                      onClick={() => setDataExpanded(v => !v)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/10 transition-colors text-left"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <span className="flex-1">Data</span>
                      <svg className={`w-3 h-3 text-white/30 transition-transform ${dataExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {dataExpanded && (
                      <div className="ml-3 mt-0.5 space-y-0.5">
                        {hasFeature('equipment') && <button
                          onClick={() => { navigate({ page: 'equipment' }); setShowLogoMenu(false) }}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors text-left ${page === 'equipment' ? 'text-white bg-white/15' : 'text-white/60 hover:text-white hover:bg-white/10'}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          Equipment
                        </button>}
                        {hasFeature('quoting') && <button
                          onClick={() => { navigate({ page: 'quoting' }); setShowLogoMenu(false) }}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors text-left ${page === 'quoting' ? 'text-white bg-white/15' : 'text-white/60 hover:text-white hover:bg-white/10'}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                          </svg>
                          Quoting
                        </button>}
                        <button
                          onClick={() => { navigate({ page: 'settings' }); setShowLogoMenu(false) }}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors text-left ${page === 'settings' ? 'text-white bg-white/15' : 'text-white/60 hover:text-white hover:bg-white/10'}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          Settings
                        </button>
                        {isEditor && hasFeature('reports') && (
                          <button
                            onClick={() => { navigate({ page: 'reports' }); setShowLogoMenu(false) }}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors text-left ${page === 'reports' ? 'text-white bg-white/15' : 'text-white/60 hover:text-white hover:bg-white/10'}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            Reports
                          </button>
                        )}
                        {isEditor && hasFeature('customer_portal') && (
                          <button
                            onClick={() => { navigate({ page: 'customer-portal' }); setShowLogoMenu(false) }}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors text-left ${page === 'customer-portal' ? 'text-white bg-white/15' : 'text-white/60 hover:text-white hover:bg-white/10'}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Customer Portal
                          </button>
                        )}
                        {isEditor && hasFeature('hangar_stack') && (
                          <button
                            onClick={() => { navigate({ page: 'hangar-stack' }); setShowLogoMenu(false) }}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors text-left ${page === 'hangar-stack' ? 'text-white bg-white/15' : 'text-white/60 hover:text-white hover:bg-white/10'}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                            </svg>
                            Hangar Stack
                          </button>
                        )}
                        {(isSuperAdmin || isEditor) && (
                          <button
                            onClick={() => { navigate({ page: 'platform-admin' }); setShowLogoMenu(false) }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors text-left text-white/60 hover:text-white hover:bg-white/10"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                            {isSuperAdmin ? 'Platform Admin' : 'Org Settings'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="mx-2 border-t border-white/10" />

                  {/* Admin */}
                  <div className="p-2">
                    <button
                      onClick={() => { setShowLogoMenu(false); setShowDeletedProjects(true) }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/10 transition-colors text-left"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Deleted Projects
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Back / Forward navigation */}
          <div className="hidden sm:flex items-center gap-0.5 mr-1">
            <button
              onClick={goBack}
              disabled={!navHistory.length}
              title="Back"
              className="p-1.5 text-white/40 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed rounded hover:bg-white/10 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={goForward}
              disabled={!navFuture.length}
              title="Forward"
              className="p-1.5 text-white/40 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed rounded hover:bg-white/10 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          {/* Refresh */}
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            title="Refresh data"
            className="p-1.5 text-white/40 hover:text-white disabled:opacity-40 disabled:cursor-default rounded hover:bg-white/10 transition-colors"
          >
            <svg className={`w-4 h-4 transition-transform ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>

          {/* Sub-view tabs — only on master schedule page */}
          {page === 'master-schedule' && (
            <div className="flex bg-white/10 rounded-lg p-1 gap-1">
              {[
                { key: 'table',       label: 'Table',           icon: 'M3 10h18M3 6h18M3 14h18M3 18h18',                techHidden: true },
                { key: 'gantt',       label: 'Master Calendar', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
                { key: 'workflows',   label: 'Workflows',       icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
                { key: 'departments', label: 'Departments',     icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0', techHidden: true },
              ]
                .filter(v => !isTechnician || !v.techHidden)
                .filter(v => !(profile?.orgs?.settings?.scheduling?.hiddenViews ?? []).includes(v.key))
                .map(v => (
                <button
                  key={v.key}
                  onClick={() => navigate({ msView: v.key })}
                  className={`flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    msView === v.key ? 'bg-brand-red text-white shadow-sm' : 'text-white/60 hover:text-white'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={v.icon} />
                  </svg>
                  <span className="hidden lg:inline">{v.label}</span>
                </button>
              ))}
            </div>
          )}

          {page === 'master-schedule' && isEditor && (
            <button
              onClick={() => { setEditProject(null); setShowProjectModal(true) }}
              className="flex items-center gap-2 bg-brand-red hover:bg-brand-red-dark text-white text-sm font-medium px-3 py-2 rounded-lg transition-colors whitespace-nowrap"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="hidden sm:inline">New Project</span>
            </button>
          )}

          {/* User menu */}
          <div className="relative pl-3 border-l border-white/20 ml-1" ref={userMenuRef}>
            <button
              onClick={() => { setShowUserMenu(v => !v); if (!showUserMenu) markAllSeen() }}
              className="flex items-center gap-2 hover:bg-white/10 rounded-lg px-2.5 py-1.5 transition-colors group"
            >
              {/* Bell with badge */}
              {!allowedModule && (
                <div className="relative">
                  <svg className="w-4 h-4 text-white/50 group-hover:text-white/80 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 rounded-full text-white text-[9px] font-bold flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </div>
              )}
              <span className="hidden sm:inline text-xs font-medium text-white/80">{profile?.display_name || user?.email}</span>
              <svg className={`w-3 h-3 text-white/30 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showUserMenu && (
              <div className="absolute right-0 top-full mt-2 w-52 bg-brand-black border border-white/10 rounded-xl shadow-2xl z-50 py-2">
                {!allowedModule && (
                  <>
                    <button
                      onClick={() => { navigate({ page: 'tasks' }); setShowUserMenu(false) }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors text-left ${page === 'tasks' ? 'text-white bg-white/15' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                      My Tasks
                    </button>
                    <div className="mx-3 my-1.5 border-t border-white/10" />
                  </>
                )}
                <button
                  onClick={() => { navigate({ page: 'profile' }); setShowUserMenu(false) }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors text-left ${page === 'profile' ? 'text-white bg-white/15' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  My Profile
                </button>
                <div className="mx-3 my-1.5 border-t border-white/10" />
                <button
                  onClick={signOut}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-white/50 hover:text-white hover:bg-white/10 transition-colors text-left"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Calendar sub-nav */}
      {page === 'master-schedule' && msView === 'workflows' && (
        <div className="no-print bg-gray-900 border-b border-black/30 px-3 md:px-6 py-2 flex items-center gap-2 overflow-x-auto">
          {[
            { key: 'project',         label: 'Project View' },
            { key: 'charts',          label: 'Hours',           techHidden: true,  feature: 'tsheets' },
            { key: 'customer-update', label: 'Customer Update', techHidden: true },
{ key: 'due-list',        label: 'Due List',        techHidden: true,  feature: 'traxxall' },
          ].filter(v => (!isTechnician || !v.techHidden) && (!v.feature || hasFeature(v.feature))).map(v => (
            <button
              key={v.key}
              onClick={() => navigate({ calendarSubView: v.key })}
              className={`text-sm px-4 py-1.5 rounded-lg font-medium transition-colors ${
                calendarSubView === v.key
                  ? 'bg-brand-red text-white'
                  : 'text-white/50 hover:text-white hover:bg-white/10'
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
      )}

      {/* Departments sub-nav */}
      {page === 'master-schedule' && msView === 'departments' && (
        <div className="no-print bg-gray-900 border-b border-black/30 px-3 md:px-6 py-2 flex items-center gap-2 overflow-x-auto">
          {[
            { key: 'monthly', label: 'Monthly' },
            { key: 'hangars', label: 'Hangars'  },
            { key: 'ac-type', label: 'AC Type'  },
          ].map(v => (
            <button
              key={v.key}
              onClick={() => navigate({ deptSubView: v.key })}
              className={`text-sm px-4 py-1.5 rounded-lg font-medium transition-colors ${
                deptSubView === v.key
                  ? 'bg-brand-red text-white'
                  : 'text-white/50 hover:text-white hover:bg-white/10'
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
      )}

      {/* Main content */}
      <div className="px-3 py-3 md:px-6 md:py-4 overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-gray-400 text-sm">Loading…</div>
        ) : (
        <Suspense fallback={<div className="flex items-center justify-center py-24 text-gray-400 text-sm">Loading…</div>}>
        {page === 'master-schedule' ? (
          msView === 'table' ? (
            <ProjectTable
              projects={filteredProjects}
              subtasks={subtasks}
              onEditProject={(p) => { setEditProject(p); setShowProjectModal(true) }}
              onAddSubtask={(id) => setAddSubtaskForProject(id)}
              onEditSubtask={(s) => setEditSubtask(s)}
              refetch={refetch}
              search={tableSearch}
              setSearch={setTableSearch}
              statusFilter={tableStatusFilter}
              toggleStatus={toggleTableStatus}
              deptFilter={tableDeptFilter}
              toggleDept={toggleTableDept}
              typeFilter={tableTypeFilter}
              toggleType={toggleTableType}
              onClearAll={clearTableFilters}
              isEditor={isEditor}
              canEditSubtasks={canEditSubtasks}
              onNavigateToWorkflow={goToWorkflow}
              onWorkOrderClick={hasFeature('ebis') ? openWorkOrder : undefined}
              userProfile={profile}
            />
          ) : msView === 'gantt' ? (
            <GanttChart
              projects={filteredProjects}
              subtasks={subtasks}
              filters={filters}
              onClickProject={(p) => openDrawer('project', p)}
              onClickSubtask={(s) => openDrawer('subtask', s)}
              onNavigateToWorkflow={goToWorkflow}
              onUpdateProjectDates={isEditor ? updateProjectDates : undefined}
              aircraft={aircraft}
              search={ganttSearch}
              setSearch={setGanttSearch}
              flightLegs={flightLegs}
              inspectionDueDates={inspectionDueDates}
              toleranceExpiryDates={toleranceExpiryDates}
              orgAircraftTypes={profile?.orgs?.settings?.aircraftTypes}
              orgDepartments={profile?.orgs?.settings?.departments}
              initialTypeFilter={tableTypeFilter}
            />
          ) : msView === 'workflows' ? (
            <CalendarView
              projects={filteredProjects}
              subtasks={subtasks}
              aircraft={aircraft}
              callouts={callouts}
              technicians={technicians}
              subtaskTechMap={subtaskTechMap}
              projectTechMap={projectTechMap}
              projectTechSet={projectTechSet}
              projectTechDates={projectTechDates}
              onAddProjectTech={addProjectTech}
              onRemoveProjectTech={removeProjectTech}
              onAddTechDate={isEditor ? handleAddTechDate : undefined}
              onRemoveTechDate={isEditor ? handleRemoveTechDate : undefined}
              subView={calendarSubView}
              onClickProject={(p) => openDrawer('project', p)}
              onClickSubtask={(s) => canEditSubtasks ? openDrawer('subtask', s) : setSubtaskNoteSheet({ subtask: s, projectId: s.project_id })}
              refetch={refetch}
              isEditor={isEditor}
              canEditSubtasks={canEditSubtasks}
              onHistory={(projectId, projectLabel) => { const proj = projects.find(p => p.id === projectId); setHistoryDrawer({ projectId, projectLabel, departure_date_baseline: proj?.departure_date_baseline, arrival_date_baseline: proj?.arrival_date_baseline, departure_date: proj?.departure_date, arrival_date: proj?.arrival_date }) }}
              onOpenNotes={openNotesModal}
              notesKey={notesKey}
              onWorkOrderClick={hasFeature('ebis') ? openWorkOrder : undefined}
              userProfile={profile}
              flightLegs={flightLegs}
              onAddToSchedule={handleAddToSchedule}
              snapshotForPrint={snapshotForPrint}
              onSnapshotPrinted={() => setSnapshotForPrint(null)}
              loadProjectSubtasks={loadProjectSubtasks}
            />
          ) : (
            <DepartmentCalendarView
              projects={filteredProjects}
              subtasks={subtasks}
              subView={deptSubView}
              onClickSubtask={(s) => goToWorkflow(s.project_id)}
              refetch={refetch}
              isEditor={isEditor}
              onAddProject={() => { setEditProject(null); setShowProjectModal(true) }}
              userProfile={profile}
              projectTechMap={projectTechMap}
              technicians={technicians}
            />
          )
        ) : page === 'capacity' ? (
          <CapacityPage
            projects={projects}
            technicians={technicians}
            projectTechnicians={projectTechnicians}
            projectTechDates={projectTechDates}
            subtasks={subtasks}
            subtaskTechMap={subtaskTechMap}
            onWorkOrderClick={hasFeature('ebis') ? openWorkOrder : undefined}
            onGoToWorkflow={goToWorkflow}
            onAddProjectTech={addProjectTech}
            onRemoveProjectTech={removeProjectTech}
          />
        ) : page === 'aircraft' ? (
          <AircraftRegistry aircraft={aircraft} refetch={refetch} isEditor={isEditor} asPage />
        ) : page === 'quoting' && hasFeature('quoting') ? (
          <QuotingPage onAddToSchedule={handleAddToSchedule} isEditor={isEditor} userProfile={profile} />
        ) : page === 'fleet-due-list' && hasFeature('traxxall') ? (
          <FleetDueListPage aircraft={aircraft} projects={projects} onGoToWorkflow={goToWorkflow} onAddToSchedule={handleAddToSchedule} flightLegs={flightLegs} />
        ) : page === 'technicians' ? (
          <TechnicianRegistry technicians={technicians} refetch={refetch} isEditor={isEditor} />
        ) : page === 'training' && hasFeature('training') ? (
          <TrainingPage isEditor={isEditor} userProfile={profile} />
        ) : page === 'reports' && isEditor && hasFeature('reports') ? (
          <ReportsPage projects={projects} subtasks={subtasks} onSelectProject={goToWorkflow} />
        ) : page === 'scheduling-dashboard' && canAccessDashboard ? (
          <SchedulingDashboardPage
            projects={projects}
            subtasks={subtasks}
            callouts={callouts}
            onGoToWorkflow={goToWorkflow}
            navigate={navigate}
            onShowDeletedProjects={() => setShowDeletedProjects(true)}
            onProjectsRefetch={fetchData}
            onOpenNotes={openNotesModal}
            isEditor={isEditor}
            dashboardRole={dashboardRole}
            linkedTech={linkedTech}
            projectTechMap={projectTechMap}
            subtaskTechMap={subtaskTechMap}
            technicians={technicians}
            flightLegs={flightLegs}
            onWorkOrderClick={hasFeature('ebis') ? openWorkOrder : undefined}
            onClickSubtask={(s) => canEditSubtasks ? openDrawer('subtask', s) : setSubtaskNoteSheet({ subtask: s, projectId: s.project_id })}
            renderQACalendar={() => (
              <DepartmentCalendarView
                projects={projects}
                subtasks={subtasks}
                subView="weekly"
                onClickSubtask={(s) => goToWorkflow(s.project_id)}
                refetch={fetchData}
                isEditor={isEditor}
                projectTechMap={projectTechMap}
                technicians={technicians}
                lockedDept="QA"
                lockedQAFilter={linkedTech?.id ?? null}
              />
            )}
          />
        ) : page === 'profile' ? (
          <ProfilePage />
        ) : page === 'tasks' ? (
          <MyTasksTab refreshKey={taskRefreshKey} onNavigateToTraining={() => navigate({ page: 'training' })} />
        ) : page === 'customer-portal' && isEditor && hasFeature('customer_portal') ? (
          <CustomerPortalPage aircraft={aircraft} refetch={refetch} initialAircraftId={deepLinkAircraftId} />
        ) : page === 'hangar-stack' && isEditor && hasFeature('hangar_stack') ? (
          <HangarStackPage onGoToWorkflow={goToWorkflow} />
        ) : page === 'equipment' && canAccessEquipment ? (
          <EquipmentPage isEditor={isEditor} isLineService={isLineService} />
        ) : page === 'settings' && isEditor ? (
          <SettingsPage aircraft={aircraft} technicians={technicians} projects={projects} refetch={refetch} />
        ) : null}
        </Suspense>
        )}
      </div>

      {/* Global EBIS work order panel */}
      {ebisWorkOrder && (
        <EBISWorkOrderPanel
          workOrderNumber={ebisWorkOrder}
          highlightItemNum={ebisHighlightItem}
          onClose={() => { setEbisWorkOrder(null); setEbisHighlightItem(null); setEbisProjectId(null) }}
          projectId={ebisProjectId}
          profileId={profile?.id}
        />
      )}

      {/* Side drawer */}
      {drawerItem && (
        <DetailDrawer
          item={drawerItem}
          subtasks={subtasks}
          subtaskTechMap={subtaskTechMap}
          projectTechMap={projectTechMap}
          isEditor={isEditor}
          canEditSubtasks={canEditSubtasks}
          onWorkOrderClick={hasFeature('ebis') ? openWorkOrder : undefined}
          onClose={() => setDrawerItem(null)}
          onHistory={(projectId, projectLabel) => {
            setDrawerItem(null)
            const proj = projects.find(p => p.id === projectId)
            setHistoryDrawer({ projectId, projectLabel, departure_date_baseline: proj?.departure_date_baseline, arrival_date_baseline: proj?.arrival_date_baseline, departure_date: proj?.departure_date, arrival_date: proj?.arrival_date })
          }}
          onSubtaskHistory={(subtask) => {
            setDrawerItem(null)
            setHistoryDrawer({ projectId: subtask.project_id, projectLabel: subtask.task_name, subtaskId: subtask.id, subtaskLabel: subtask.task_name, subtaskStartDate: subtask.start_date, subtaskEndDate: subtask.end_date })
          }}
          onEdit={(type, data) => {
            setDrawerItem(null)
            if (type === 'project') {
              setEditProject(data)
              setShowProjectModal(true)
            } else {
              setEditSubtask(data)
            }
          }}
          onAddSubtask={(projectId) => {
            setDrawerItem(null)
            setAddSubtaskForProject(projectId)
          }}
          onDelete={async (data, type) => {
            if (type === 'project') {
              if (!confirm(`Move project ${data.aircraft?.registration || ''} to trash? You can restore it from Admin → Deleted Projects.`)) return
              await logChange({
                projectId: data.id, entityType: 'project', entityId: data.id,
                entityLabel: data.aircraft?.registration, action: 'deleted', userProfile: profile,
              })
              await deleteProject(data.id)
              setDrawerItem(null)
              await fetchData()
            } else {
              if (!confirm(`Delete subtask "${data.task_name}"?`)) return
              const projectId = data.project_id
              await logChange({
                projectId, entityType: 'subtask', entityId: data.id,
                entityLabel: data.task_name, action: 'deleted', userProfile: profile,
              })
              await deleteSubtask(data.id)
              setDrawerItem(null)
              await fetchData()
              if (projectId) {
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    const project = projects.find(p => p.id === projectId)
                    if (project) openDrawer('project', project)
                  })
                })
              }
            }
          }}
          onColorSaved={refetch}
          refetch={refetch}
        />
      )}

      {/* History drawer */}
      {historyDrawer && (
        <ProjectHistoryDrawer
          projectId={historyDrawer.projectId}
          projectLabel={historyDrawer.projectLabel}
          departure_date_baseline={historyDrawer.departure_date_baseline}
          arrival_date_baseline={historyDrawer.arrival_date_baseline}
          departure_date={historyDrawer.departure_date}
          arrival_date={historyDrawer.arrival_date}
          subtaskId={historyDrawer.subtaskId}
          subtaskLabel={historyDrawer.subtaskLabel}
          subtaskStartDate={historyDrawer.subtaskStartDate}
          subtaskEndDate={historyDrawer.subtaskEndDate}
          onClose={() => setHistoryDrawer(null)}
          onPrintSnapshot={(snap) => setSnapshotForPrint(snap)}
        />
      )}

      {/* Project Notes Modal */}
      {notesModal && (
        <ProjectNotesModal
          projectId={notesModal.projectId}
          projectReg={notesModal.reg}
          required={notesModal.required}
          prompt={notesModal.prompt}
          userProfile={profile}
          canAddProjectNote={isEditor || isSubtaskEditor}
          onClose={() => { setNotesModal(null); setNotesKey(k => k + 1) }}
        />
      )}

      {subtaskNoteSheet && (
        <SubtaskNoteSheet
          subtask={subtaskNoteSheet.subtask}
          projectId={subtaskNoteSheet.projectId}
          userProfile={profile}
          onClose={() => setSubtaskNoteSheet(null)}
        />
      )}

      {/* Modals */}
      {showProjectModal && (
        <ProjectModal
          project={editProject}
          prefill={editProject ? null : projectPrefill}
          subtasks={subtasks}
          callouts={callouts}
          aircraft={aircraft}
          userProfile={profile}
          onClose={() => { setShowProjectModal(false); setEditProject(null); setProjectPrefill(null) }}
          onSaved={async () => {
            setShowProjectModal(false)
            const savedProject = editProject
            setEditProject(null)
            setProjectPrefill(null)
            await fetchData()
            if (savedProject) {
              requestAnimationFrame(() => {
                const updated = projects.find(p => p.id === savedProject.id)
                if (updated) openDrawer('project', updated)
              })
            }
          }}
          onScheduleStatusChanged={(projectId, oldStatus, newStatus) => {
            const proj = projects.find(p => p.id === projectId)
            const reg = proj?.aircraft?.registration || proj?.secondary_registration || '—'
            const labels = { ahead: 'Ahead of Schedule', tracking: 'On Track', at_risk: 'At Risk', delayed: 'Delayed' }
            openNotesModal(projectId, reg, { required: true, prompt: `Risk level changed from "${labels[oldStatus] || oldStatus}" to "${labels[newStatus] || newStatus}" — please add a note explaining why.` })
          }}
          onSubtasksUpdated={fetchData}
        />
      )}

      {editSubtask && (
        <SubtaskModal
          projectId={editSubtask.project_id}
          subtask={editSubtask}
          userProfile={profile}
          technicians={technicians}
          onClose={() => setEditSubtask(null)}
          onSaved={async () => {
            const projectId = editSubtask?.project_id
            setEditSubtask(null)
            await fetchData()
            if (projectId) {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  const project = projects.find(p => p.id === projectId)
                  if (project) openDrawer('project', project)
                })
              })
            }
          }}
        />
      )}

      {addSubtaskForProject && (
        <SubtaskModal
          projectId={addSubtaskForProject}
          subtask={null}
          userProfile={profile}
          technicians={technicians}
          onClose={() => setAddSubtaskForProject(null)}
          onSaved={async () => {
            const projectId = addSubtaskForProject
            setAddSubtaskForProject(null)
            await fetchData()
            if (projectId) {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  const project = projects.find(p => p.id === projectId)
                  if (project) openDrawer('project', project)
                })
              })
            }
          }}
        />
      )}

      {showDeletedProjects && (
        <DeletedProjectsModal
          userProfile={profile}
          onClose={() => setShowDeletedProjects(false)}
          onRestored={() => { refetch() }}
        />
      )}

      {showDailyDigest && (
        <DailyDigestModal
          onClose={() => setShowDailyDigest(false)}
          onGoToTasks={() => { markDailyDigestShown(user?.id); setShowDailyDigest(false); navigate({ page: 'tasks' }) }}
          onGoToProject={(projectId) => { markDailyDigestShown(user?.id); setShowDailyDigest(false); goToWorkflow(projectId) }}
          dashboardRole={dashboardRole}
          isTechnician={isTechnician}
          linkedTech={linkedTech}
          projects={projects}
          subtasks={subtasks}
          projectTechMap={projectTechMap}
        />
      )}

      {showWarning && (
        <IdleWarningModal
          secondsLeft={secondsLeft}
          onStay={stayLoggedIn}
          onSignOut={signOut}
        />
      )}
    </div>
  )
}

// ── Auth gate ─────────────────────────────────────────────────────────────────

function AppContent() {
  const { user, profile, loading, needsPasswordSetup, completePasswordSetup, mustChangePassword, clearMustChangePassword } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-400 text-sm">Loading…</div>
      </div>
    )
  }

  if (needsPasswordSetup) {
    return <SetPasswordPage onDone={completePasswordSetup} />
  }

  if (!user) return <LoginPage />

  // Profile fetch completed but returned null — transient network/DB error.
  // Reloading retries the full auth + profile sequence.
  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-3">
        <div className="text-gray-500 text-sm">Unable to load your profile.</div>
        <button
          onClick={() => window.location.reload()}
          className="text-sm text-blue-600 hover:text-blue-800 underline"
        >
          Retry
        </button>
      </div>
    )
  }

  if (mustChangePassword) {
    return <SetPasswordPage onDone={clearMustChangePassword} />
  }

  return <AuthenticatedApp />
}

export default function App() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine)

  useEffect(() => {
    const onOnline  = () => setIsOffline(false)
    const onOffline = () => setIsOffline(true)
    window.addEventListener('online',  onOnline)
    window.addEventListener('offline', onOffline)
    return () => {
      window.removeEventListener('online',  onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [])

  // Public routes — no staff auth required
  if (window.location.pathname.startsWith('/portal')) {
    return <Suspense fallback={null}><CustomerPortal /></Suspense>
  }

  return (
    <AuthProvider>
      <AppContent />
      {isOffline && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-gray-900 text-white text-xs font-medium px-4 py-2.5 rounded-full shadow-lg pointer-events-none select-none">
          <div className="w-1.5 h-1.5 rounded-full bg-orange-400" />
          Offline — showing last synced data
        </div>
      )}
    </AuthProvider>
  )
}
